import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ClusterDetailAPI } from "../../../../api/specialization/ClusterDetailAPI";
import { Button, Grid, List, Pagination, Typography } from "@mui/material";
import RandomColor from "../../../../util/RandomColor";
import { singleField, sumByField } from "../../../../util/ArrayUtil";
import { curveLineOptions } from "../../../../util/ChartStyleUtil";
import { Bar, Line, Pie, getElementsAtEvent } from "react-chartjs-2";
import BrowseGalleryIcon from "@mui/icons-material/BrowseGallery";
import { Chart, Chart as ChartJS, registerables } from "chart.js";
import { useTranslation } from "react-i18next";
import ContentListPopup from "../../../../components/popup/ContentListPopup";
import { findContentFlexFilter } from "../../../../api/content/ContentAPI";
import { errorNoti } from "../../../../util/SnackbarMessage";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useSnackbar } from "notistack";
import SignalWifi4BarIcon from "@mui/icons-material/SignalWifi4Bar";
import SignalWifi3BarIcon from "@mui/icons-material/SignalWifi3Bar";
import SignalWifi2BarIcon from "@mui/icons-material/SignalWifi2Bar";
import SignalWifi1BarIcon from "@mui/icons-material/SignalWifi1Bar";
import { WordBarElement } from "../../../../components/WordBarElement";
import { getKeywordDetail } from "../../../../api/statistics/keyword/KeywordStatisticsAPI";
import { KeywordDetailPopup } from "../KeywordDetailPopup";
import { WordBubbleWrapper } from "../../../../components/WordBubbleWrapper";
import NumberBox from "../../../../components/NumberBox";
import AssistantPhotoIcon from "@mui/icons-material/AssistantPhoto";
import TrendzContainerCard from "../../../../components/TrendzContainerCard";
ChartJS.register(...registerables);

type SentimentDateData = {
  date: string;
  positive?: number;
  negative?: number;
  neutral?: number;
};

export function KeywordClusterReport() {
  const [cluster, setCluster] = useState({ name: "", relatedKeywords: [] });
  const [clusterAppearances, setClusterAppearances] = useState([]);
  const [clusterSources, setClusterSources] = useState([]);
  const [clusterNegativesCount, setClusterNegativesCount] = useState([]);
  const [clusterPositiveCount, setClusterPositiveCount] = useState([]);
  const [clusterNeutralCount, setClusterNeutralCount] = useState([]);
  const [clusterRelatedKeywords, setClusterRelatedKeywords] = useState([]);
  const [chosenDetailKeyword, setChosenDetailKeyword] = useState("");
  const [chosenDetailKeywordData, setChosenDetailKeywordData] = useState({});
  const [keywordDetailPopup, setKeywordDetailPopup] = useState(false);
  const [keywordPage, setKeywordPage] = useState(1);
  const { t } = useTranslation(["common", "keyword", "error", "sidebar"]);
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (chosenDetailKeyword.length > 0) {
      getKeywordDetail(chosenDetailKeyword, 7)
        .then((res) => {
          console.log(res);
          setChosenDetailKeywordData(res);
        })
        .catch((err) => {
          var noti = errorNoti(err);
          enqueueSnackbar(t("error:" + noti.message), noti.option);
        });
    }
  }, [chosenDetailKeyword]);

  useEffect(() => {
    if (id) {
      ClusterDetailAPI(parseInt(id))
        .then((res) => {
          setCluster(res["cluster"]);
          setClusterAppearances(res["clusterAppearances"]);
          setClusterSources(res["clusterSources"]);
          setClusterNegativesCount(res["clusterNegativesCount"]);
          setClusterPositiveCount(res["clusterPositiveCount"]);
          setClusterNeutralCount(res["clusterNeutralCount"]);
          setClusterRelatedKeywords(res["clusterRelatedKeywords"]);
        })
        .catch((err) => {
          var noti = errorNoti(err);
          enqueueSnackbar(t("error:" + noti.message), noti.option);
        });
    }
  }, []);

  let colors = new Map(
    cluster.relatedKeywords.map((keyword) => [
      keyword,
      RandomColor({ name: keyword }),
    ])
  );

  var keywordTotalAppearanceCount = new Map();

  cluster.relatedKeywords.forEach((keyword) => {
    keywordTotalAppearanceCount.set(keyword, 0);
  });

  clusterAppearances.forEach((app) => {
    keywordTotalAppearanceCount.set(
      app[0],
      keywordTotalAppearanceCount.get(app[0]) + app[2]
    );
  });

  var keywordTotalAppearanceCountList = [
    ...keywordTotalAppearanceCount.entries(),
  ].sort((a, b) => b[1] - a[1]);

  var maxAppearanceCount =
    keywordTotalAppearanceCountList.length > 0
      ? keywordTotalAppearanceCountList[0][1]
      : 1;

  var keywordList = keywordTotalAppearanceCountList
    .slice((keywordPage - 1) * 5, keywordPage * 5)
    .map((keyword) => {
      var barLength = (keyword[1] / maxAppearanceCount) * 100;
      var icon = <SignalWifi1BarIcon color="info" />;
      if (barLength > 20) {
        icon = <SignalWifi1BarIcon color="info" />;
      }
      if (barLength > 40) {
        icon = <SignalWifi2BarIcon color="info" />;
      }
      if (barLength > 60) {
        icon = <SignalWifi3BarIcon color="info" />;
      }
      if (barLength > 80) {
        icon = <SignalWifi4BarIcon color="info" />;
      }
      return (
        <div style={{ display: "flex", marginRight: "30px" }} className="">
          <WordBarElement
            word={keyword[0]}
            scaledNumber={barLength}
            color="secondary"
            icon={icon}
          />
          <Button
            variant="contained"
            style={{ minWidth: "100px", margin: "10px" }}
            onClick={() => {
              setChosenDetailKeyword(keyword[0]);
              setKeywordDetailPopup(true);
            }}
          >
            {t("common:detail")}
          </Button>
        </div>
      );
    });

  const relatedKeywords = [...new Set(cluster.relatedKeywords)];

  var clusterAppearancesSorted = clusterAppearances.sort((a: any, b: any) => {
    var aComps = (a[1] as string).split("-");
    var bComps = (b[1] as string).split("-");
    var aDate = new Date(
      parseInt(aComps[0]),
      parseInt(aComps[1]),
      parseInt(aComps[2])
    );
    var bDate = new Date(
      parseInt(bComps[0]),
      parseInt(bComps[1]),
      parseInt(bComps[2])
    );
    return aDate.getTime() - bDate.getTime();
  });

  const dayAppearanceLabels = [
    ...new Set(
      clusterAppearancesSorted.map((appearance) => {
        return appearance[1];
      })
    ),
  ];

  function appearanceCount(keyword: string) {
    return clusterAppearancesSorted.filter((appearance) => {
      return (appearance[0] as any).toLowerCase() == keyword.toLowerCase();
    });
  }

  const appearancesDatasets = relatedKeywords.map(function (keyword) {
    const counts = appearanceCount(keyword).map((appearance) => appearance[2]);
    return {
      label: keyword,
      data: counts,
      backgroundColor: colors.get(keyword),
      borderColor: colors.get(keyword),
    };
  });

  const appearanceData = {
    labels: dayAppearanceLabels,
    datasets: appearancesDatasets,
  };

  var appearanceOptions = {
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        position: "right" as const,
        display: true,
      },
    },
    responsive: true,
    elements: curveLineOptions().elements,
  };

  var clusterSourcesSorted = clusterSources.sort((a, b) => {
    return b[1] - a[1];
  });

  const sourceOptions = {
    plugins: {
      legend: { position: "bottom" as const, display: true },
      title: {
        display: false,
        text: "Nguồn các bài viết về cụm từ khóa " + cluster.name,
      },
    },
    responsive: true,
  };

  const sourceLabels = clusterSourcesSorted.map((clusterSource) => {
    return clusterSource[0];
  });

  const sourceCounts = clusterSourcesSorted.map((clusterSource) => {
    return clusterSource[1];
  });

  let sourceColors = clusterSources.map((clusterSource) =>
    RandomColor({ name: clusterSource[0] })
  );

  const sourceDatasets = [
    {
      label: "sourceLabels",
      data: sourceCounts.slice(0, 5),
      backgroundColor: sourceColors.slice(0, 5),
    },
  ];

  const sourceData = {
    labels: sourceLabels.slice(0, 5),
    datasets: sourceDatasets,
  };

  let sentimentArrays = [];

  let clusterNegativeCountDetail = clusterNegativesCount.map((nCount) => {
    return { date: nCount[0], negative: nCount[1] };
  });

  let clusterPositiveCountDetail = clusterPositiveCount.map((pCount) => {
    return { date: pCount[0], positive: pCount[1] };
  });

  let clusterNeutralCountDetail = clusterNeutralCount.map((pCount) => {
    return { date: pCount[0], neutral: pCount[1] };
  });

  for (let i = 0; i < clusterNegativeCountDetail.length; i++) {
    sentimentArrays.push({
      ...(clusterNegativeCountDetail[i] as object),
      ...(clusterPositiveCountDetail.find(
        (positive) => positive.date == clusterNegativeCountDetail[i].date
      ) as unknown as SentimentDateData),
      ...clusterNeutralCountDetail.find(
        (neutral) => neutral.date == clusterNegativeCountDetail[i].date
      ),
    });
  }

  for (let i = 0; i < clusterPositiveCountDetail.length; i++) {
    var toPush = {
      ...(clusterPositiveCountDetail[i] as object),
      ...(clusterNegativeCountDetail.find(
        (negative) => negative.date == clusterPositiveCountDetail[i].date
      ) as unknown as SentimentDateData),
      ...(clusterNeutralCountDetail.find(
        (neutral) => neutral.date == clusterPositiveCountDetail[i].date
      ) as unknown as SentimentDateData),
    };
    sentimentArrays.push(toPush);
  }

  for (let i = 0; i < clusterNeutralCountDetail.length; i++) {
    var toPush = {
      ...(clusterNeutralCountDetail[i] as object),
      ...(clusterNegativeCountDetail.find(
        (negative) => negative.date == clusterNeutralCountDetail[i].date
      ) as unknown as SentimentDateData),
      ...(clusterPositiveCountDetail.find(
        (positive) => positive.date == clusterNeutralCountDetail[i].date
      ) as unknown as SentimentDateData),
    };
    sentimentArrays.push(toPush);
  }

  sentimentArrays = [
    ...new Set(
      sentimentArrays.map((entry) => {
        var obj = {
          date: entry.date,
          positive: entry.positive ? entry.positive : 0,
          negative: entry.negative ? entry.negative : 0,
          neutral: entry.neutral ? entry.neutral : 0,
        };
        return JSON.stringify(obj);
      })
    ),
  ];

  sentimentArrays = sentimentArrays.map((entry) => {
    return JSON.parse(entry);
  });

  sentimentArrays = sentimentArrays.sort((a: any, b: any) => {
    var aComps = (a.date as string).split("-");
    var bComps = (b.date as string).split("-");
    var aDate = new Date(
      parseInt(aComps[0]),
      parseInt(aComps[1]),
      parseInt(aComps[2])
    );
    var bDate = new Date(
      parseInt(bComps[0]),
      parseInt(bComps[1]),
      parseInt(bComps[2])
    );
    return aDate.getTime() - bDate.getTime();
  });

  const sentimentLabels = singleField(sentimentArrays, "date");

  const sentimentData = {
    labels: sentimentLabels,
    datasets: [
      {
        label: t("common:negative"),
        data: singleField(sentimentArrays, "negative"),
        backgroundColor: "#EF4C59",
      },

      {
        label: t("common:neutral"),
        data: singleField(sentimentArrays, "neutral"),
        backgroundColor: "#81d4fa",
      },

      {
        label: t("common:positive"),
        data: singleField(sentimentArrays, "positive"),
        backgroundColor: "#34E82C",
      },
    ],
  };

  const [sourceContentPopup, setSourceContentPopup] = useState(false);
  const [appearanceContentPopup, setAppearanceContentPopup] = useState(false);
  const sourceChartRef = useRef();

  const onCloseSourcePopup = () => {
    setSourceContentPopup(false);
    setChosenSource("");
    setChosenPopupTitle("");
    setContents([]);
  };

  const onCloseAppearancePopup = () => {
    setChosenKeyword("");
    setAppearanceContentPopup(false);
    setChosenPopupTitle("");
    setContents([]);
  };

  const appearanceChartRef = useRef();

  const [contents, setContents] = useState([]);

  const [chosenPopupTitle, setChosenPopupTitle] = useState("");

  const [chosenSource, setChosenSource] = useState("");

  const [chosenKeyword, setChosenKeyword] = useState("");

  useEffect(() => {
    if (chosenKeyword.length > 0) {
      findContentFlexFilter({
        keyword: chosenKeyword,
        days: 3,
      })
        .then((res) => {
          setContents(res);
          setChosenPopupTitle(
            t("keyword:recent_articles") +
              " " +
              t("common:about") +
              " " +
              chosenKeyword
          );
        })
        .catch((err) => {
          var noti = errorNoti(err);
          enqueueSnackbar(t("error:" + noti.message), noti.option);
        });
    }
  }, [chosenKeyword]);

  useEffect(() => {
    if (chosenSource.length > 0) {
      findContentFlexFilter({
        keyword: cluster.relatedKeywords[0],
        days: 3,
        source: chosenSource,
        adjacentKeyword: cluster.relatedKeywords[1]
          ? cluster.relatedKeywords[1]
          : "",
      })
        .then((res) => {
          setContents(res);
          setChosenPopupTitle(
            t("keyword:articles_from_site") +
              " " +
              chosenSource +
              " " +
              t("common:about") +
              " " +
              cluster.name
          );
        })
        .catch((err) => {
          var noti = errorNoti(err);
          enqueueSnackbar(t("error:" + noti.message), noti.option);
        });
    }
  }, [chosenSource]);

  const onSourceClick = (event: any) => {
    if (
      getElementsAtEvent(sourceChartRef.current as unknown as Chart, event)
        .length > 0
    ) {
      const index = getElementsAtEvent(
        sourceChartRef.current as unknown as Chart,
        event
      )[0].index;
      setSourceContentPopup(true);
      setChosenSource(sourceLabels[index]);
    }
  };

  const onAppearanceClick = (event: any) => {
    if (
      getElementsAtEvent(appearanceChartRef.current as unknown as Chart, event)
        .length > 0
    ) {
      const index = Chart.getChart(
        appearanceChartRef.current as unknown as any
      )?.getElementsAtEventForMode(
        event,
        "y",
        { axis: "x", intersect: false },
        true
      )[0].datasetIndex;
      setSourceContentPopup(true);
      setChosenKeyword(appearancesDatasets[index ? index : 0].label);
    }
  };

  var relatedKeywordsBubbleData = clusterRelatedKeywords
    .slice(0, 23)
    .map((keywordData) => {
      var color = RandomColor({ name: keywordData[0] });
      return {
        name: keywordData[0],
        points: keywordData[2],
        color: color,
      };
    });

  const closePopup = () => {
    setKeywordDetailPopup(false);
    setChosenDetailKeyword("");
  };

  console.log(relatedKeywords);

  return (
    <div>
      {chosenDetailKeyword.length > 0 ? (
        <KeywordDetailPopup
          open={keywordDetailPopup}
          onClose={closePopup}
          keywordDetail={chosenDetailKeywordData}
          keyword={chosenDetailKeyword}
        />
      ) : (
        ""
      )}
      <Grid container marginTop={4} marginBottom={4}>
        <Grid item xs={0.25}></Grid>
        <Grid
          item
          xs={3.25}
          className="grid-border white"
          textAlign={"center"}
          padding={2}
        >
          <NumberBox
            title={t("keyword:top_keyword")}
            variant={"h3"}
            number={
              keywordTotalAppearanceCountList.length > 0
                ? keywordTotalAppearanceCountList[0][0]
                : ""
            }
            icon={
              <EmojiEventsIcon
                color="warning"
                sx={{ width: "40px", height: "40px" }}
              />
            }
            subtitle={t("common:derived_from_this_week")}
          />
        </Grid>
        <Grid item xs={0.75}></Grid>
        <Grid
          item
          xs={3.5}
          className="grid-border white"
          textAlign={"center"}
          padding={2}
        >
          <NumberBox
            title={t("keyword:appearances_week")}
            variant={"h3"}
            number={sumByField(clusterAppearancesSorted, 2)}
            icon={
              <BrowseGalleryIcon
                sx={{ width: "40px", height: "40px", color: "#81d4fa" }}
              />
            }
            subtitle={t("keyword:derived_from_all_keywords_in_cluster")}
          />
        </Grid>
        <Grid item xs={0.75}></Grid>
        <Grid
          item
          xs={3.25}
          className="grid-border white"
          textAlign={"center"}
          padding={2}
        >
          <NumberBox
            title={t("keyword:appearances_today")}
            variant={"h3"}
            number={sumByField(
              clusterAppearancesSorted.filter((e) => {
                console.log(Date.parse(e[1]));
                const date = new Date();
                let currentDate = `${date.getFullYear()}-${
                  date.getMonth() + 1
                }-${date.getDate()}`;
                console.log(currentDate);
                console.log(Date.parse(currentDate));
                return Date.parse(e[1]) == Date.parse(currentDate);
              }),
              2
            )}
            icon={
              <AssistantPhotoIcon
                color="success"
                sx={{ width: "40px", height: "40px" }}
              />
            }
            subtitle={t("keyword:derived_from_all_keywords_in_cluster")}
          />
        </Grid>
        <Grid item xs={0.25}></Grid>
      </Grid>
      <Grid container marginBottom={4}>
        <Grid item xs={0.25}></Grid>
        <Grid item xs={5.5} className="grid-border  white" padding={2}>
          <Typography variant="subtitle1" textAlign={"left"} fontWeight={600}>
            {t("keyword:keyword_appearances_for_cluster") +
              " '" +
              cluster.name +
              "'"}
          </Typography>
          <div>
            <Line
              width={"100%"}
              height={"35%"}
              data={appearanceData}
              options={appearanceOptions}
              ref={appearanceChartRef}
              onClick={onAppearanceClick}
            />
            <ContentListPopup
              popup={appearanceContentPopup}
              onClosePopup={onCloseAppearancePopup}
              contents={contents}
              title={chosenPopupTitle}
            />
          </div>
        </Grid>
        <Grid item xs={0.5}></Grid>
        <Grid item xs={5.5} className="grid-border  white" padding={2}>
          <Typography variant="subtitle1" textAlign={"left"} fontWeight={600}>
            {t("keyword:sentiment_for_cluster") + " '" + cluster.name + "'"}
          </Typography>
          <Bar
            width={"100%"}
            height={"35%"}
            options={{
              scales: {
                x: {
                  grid: {
                    display: true,
                  },
                },
                y: {
                  grid: {
                    display: false,
                  },
                },
              },
            }}
            data={sentimentData}
          ></Bar>
        </Grid>
        <Grid item xs={0.25}></Grid>
      </Grid>

      <Grid container marginBottom={4}>
        <Grid item xs={0.25}></Grid>
        <Grid
          item
          xs={5.5}
          className="grid-border  white"
          style={{ paddingLeft: "16px", paddingTop: "16px" }}
        >
          <Typography variant="subtitle1" textAlign={"left"} fontWeight={600}>
            {t("keyword:sources_for_cluster") + " '" + cluster.name + "'"}
          </Typography>
          <div
            style={{ marginLeft: "auto", marginRight: "auto", width: "50%" }}
          >
            <Pie
              options={sourceOptions}
              data={sourceData}
              ref={sourceChartRef}
              onClick={onSourceClick}
            ></Pie>
            <ContentListPopup
              popup={sourceContentPopup}
              onClosePopup={onCloseSourcePopup}
              contents={contents}
              title={chosenPopupTitle}
            ></ContentListPopup>
          </div>
        </Grid>
        <Grid item xs={0.5}></Grid>
        <Grid
          item
          xs={5.5}
          className="grid-border white "
          alignItems="center"
          justifyContent="center"
          style={{ paddingLeft: "16px", paddingTop: "16px" }}
        >
          <Typography
            variant="subtitle1"
            textAlign={"left"}
            fontWeight={600}
            className=""
          >
            {t("keyword:popularity") +
              " " +
              t("common:of") +
              " " +
              t("common:keywords")}
          </Typography>
          <List>{keywordList}</List>
          {keywordTotalAppearanceCountList.length > 0 ? (
            <Pagination
              id="keywords-pagination"
              className=""
              count={Math.max(
                Math.ceil(keywordTotalAppearanceCountList.length / 5),
                1
              )}
              page={keywordPage}
              onChange={(event: any, page: any) => setKeywordPage(page)}
              color="primary"
              variant="text"
              size="medium"
              sx={{
                marginTop: "10px !important",
                marginBottom: "10px !important",
              }}
            ></Pagination>
          ) : (
            ""
          )}
        </Grid>
        <Grid item xs={0.25}></Grid>
      </Grid>
      <Grid container>
        <Grid item xs={0.25}></Grid>
        <Grid item xs={5.5} className="grid-border  white" padding={2}>
          <Typography variant="subtitle1" textAlign={"center"} fontWeight={600}>
            {t("keyword:related_keywords") +
              " " +
              t("keyword:to_cluster") +
              " '" +
              cluster.name +
              "'"}
          </Typography>
          <WordBubbleWrapper
            data={relatedKeywordsBubbleData}
            width={700}
            height={600}
            showLegend={true}
          />
        </Grid>
        <Grid item xs={0.5}></Grid>
        <Grid item xs={5.5} className="grid-border  white" padding={2}>
          <Typography variant="subtitle1" textAlign={"center"} fontWeight={600}>
            {t("keyword:other_statistics") +
              " " +
              t("keyword:about_cluster") +
              " '" +
              cluster.name +
              "'"}
          </Typography>
        </Grid>
        <Grid item xs={0.25}></Grid>
      </Grid>
    </div>
  );
}
